/**
 * The customers, services and ZIP rule that the RULES layer actually knows about.
 *
 * WHY THIS FILE EXISTS: UC2 and UC4 are the same six-table join against two different engines, so
 * the only honest difference between their forms is the endpoint. Before this file they disagreed
 * — and both disagreed with the database:
 *
 *   1. UC2's service picker offered `FIRST_CLASS`, `GROUND_ADVANTAGE` and `PRIORITY_EXPRESS`.
 *      NONE of those exist. Verified live: all three return
 *      `{"error":"unknown serviceCode ...","code":"NOT_FOUND"}`. Three of the four options in a
 *      demo dropdown could not resolve anything, which makes the picker look like the engine is
 *      broken when in fact the CLIENT was offering codes that were never seeded.
 *   2. Both panels validated the destination ZIP as `^[0-9]{5}$`. Construct C01 requires a
 *      9-digit ZIP+4 and the API says so explicitly — `rule initech_intl_medium selects construct
 *      C01, which requires a 9-digit ZIP+4; destZip was 5 digits. Supply the full ZIP+4 (padding
 *      it would fabricate routing data)`. So the client-side check made C01 UNREACHABLE from the
 *      UI, and the reachable-construct count was silently one lower than the data supports.
 *
 * Every value below is seeded by `services/rules-api/dsql-schema.sql` (identical to `schema.sql`,
 * which is the point — the two engines hold the same rows so the only variable is the engine) and
 * was CONFIRMED against the deployed API, not read off the file. Where a row's status makes it
 * fail, that is recorded here rather than hidden, because a refusal is a demonstration too: it is
 * the difference between "the rule engine ran and said no" and "the client sent a code that does
 * not exist".
 *
 * DO NOT ADD A CONSTRUCT PICKER TO UC2 OR UC4 FROM THIS FILE. UC1 lets the caller choose the
 * construct because `POST /v1/serials` takes one — the construct is genuinely an input there. In
 * UC2/UC4 the construct is the RESOLVED OUTPUT of the rules, so a dropdown would let a viewer
 * override the very engine being demonstrated. Switching CUSTOMER is the honest way to move the
 * construct, which is what `CUSTOMER_OPTIONS` is for.
 */

/**
 * The four seeded customers. `hooli` is SUSPENDED in the database and is included DELIBERATELY:
 * it is the only option that proves the engine enforces customer status, and omitting it would
 * leave the panel unable to show a refusal that came from data rather than from validation.
 */
export const CUSTOMER_OPTIONS = [
  {
    value: 'acme',
    label: 'acme — ACME Corporation (ENTERPRISE)',
    description: 'PRIORITY → C03 · GROUND → C04. Has two customer-specific PRIORITY rules, one gated on ZIP prefix 9.',
  },
  {
    value: 'globex',
    label: 'globex — Globex Corporation (ENTERPRISE)',
    description: 'PRIORITY → C07 via globex_priority_compact (6-digit MID, channel AI 93). GROUND subscription is SUSPENDED.',
  },
  {
    value: 'initech',
    label: 'initech — Initech LLC (STANDARD)',
    description: 'INTL → C01, which needs a 9-digit ZIP+4. GROUND → C04. Not subscribed to PRIORITY.',
  },
  {
    value: 'hooli',
    label: 'hooli — Hooli Inc (SUSPENDED)',
    description: 'Customer status is SUSPENDED, so every vend is refused by the rules — a refusal from DATA, not from client validation.',
  },
];

/**
 * The four seeded service codes. `FIRSTCLASS` is RETIRED and kept for the same reason as `hooli`:
 * it produces `service FIRSTCLASS is retired` from the engine, which is a different and more
 * interesting failure than a code that was never defined.
 */
export const SERVICE_OPTIONS = [
  { value: 'PRIORITY', label: 'PRIORITY — Priority Mail' },
  { value: 'GROUND', label: 'GROUND — USPS Ground Advantage' },
  { value: 'INTL', label: 'INTL — Priority Mail International' },
  { value: 'FIRSTCLASS', label: 'FIRSTCLASS — First-Class Package (RETIRED)' },
];

/** Which construct each customer/service pair resolves to, as OBSERVED from the deployed API. */
export const OBSERVED_RESOLUTIONS = [
  { customerId: 'acme', serviceCode: 'PRIORITY', construct: 'C03', stc: '123', rule: 'acme_priority_light_nsa' },
  { customerId: 'acme', serviceCode: 'GROUND', construct: 'C04', stc: '031', rule: 'ground_light_default' },
  { customerId: 'globex', serviceCode: 'PRIORITY', construct: 'C07', stc: '777', rule: 'globex_priority_compact' },
  { customerId: 'initech', serviceCode: 'INTL', construct: 'C01', stc: '042', rule: 'initech_intl_medium' },
];

export const DEFAULT_CUSTOMER = CUSTOMER_OPTIONS[0];
export const DEFAULT_SERVICE = SERVICE_OPTIONS[0];

/** Human help for the ZIP field, stating both accepted widths and why 9 matters. */
export const ZIP_DESCRIPTION =
  '5 digits (ZIP5) or 9 (ZIP+4). C01 REQUIRES 9 — the API refuses to pad, because padding would fabricate routing data.';

/**
 * Validate the destination ZIP the way the SERVER does, rather than more strictly.
 *
 * The server accepts 5 or 9 digits and then lets the RESOLVED construct decide whether 9 was
 * required. A client that only accepts 5 does not prevent an error — it prevents a SUCCESS, and it
 * does so invisibly. Returns null when valid, else the message to show.
 */
export function zipProblem(destZip) {
  const z = String(destZip || '').trim();
  if (/^[0-9]{5}$/.test(z) || /^[0-9]{9}$/.test(z)) return null;
  if (/^[0-9]{5}-[0-9]{4}$/.test(z)) {
    return 'destination ZIP must be digits only — the API rejects the hyphenated ZIP+4 form, so strip the dash (e.g. 202600001)';
  }
  return 'destination ZIP must be exactly 5 digits (ZIP5) or 9 digits (ZIP+4)';
}

/** Ounces, validated the way both /v2 and /v4 do. Returns {oz} or {error}. */
export function parseWeightOz(weightOz) {
  const oz = Number(weightOz);
  if (!Number.isFinite(oz) || oz < 1) {
    return { error: 'weight must be a positive number of ounces' };
  }
  return { oz };
}

/**
 * The weight bands, so a panel can say WHICH band a weight lands in before the round trip and the
 * viewer can see that the band is part of what selects the rule.
 */
export const WEIGHT_BANDS = [
  { bandCode: 'LIGHT', minOz: 0, maxOz: 15 },
  { bandCode: 'MEDIUM', minOz: 16, maxOz: 160 },
  { bandCode: 'HEAVY', minOz: 161, maxOz: 1120 },
];

export function bandForOz(oz) {
  const b = WEIGHT_BANDS.find((w) => oz >= w.minOz && oz <= w.maxOz);
  return b ? b.bandCode : null;
}

/** Find the option object for a stored value, so state can round-trip through a Select. */
export function optionFor(options, value) {
  return options.find((o) => o.value === value) || options[0];
}
